/**
 * `MyCv` — pagina "Il mio CV" del Self_User (utente autenticato).
 *
 * Rotta `/me/cv` (protetta, accessibile a ogni ruolo autenticato; la
 * registrazione della rotta e la voce di menu sono demandate ai task 11.2/11.3).
 * La pagina riusa interamente la vista condivisa {@link ExternalCvProfileView}
 * in Editable_Mode (`editable`), senza duplicare alcun componente di
 * visualizzazione: il flusso dei CV esterni resta in sola lettura.
 *
 * Dati e mutazioni (tutte self-only, lo `userId` è ricavato dalla sessione JWT
 * lato API — nessun parametro target):
 *  - `GET    /api/me/cv`                                   → `MyCvDto`;
 *  - `PUT    /api/me/cv/sections`                          → aggiorna una sezione;
 *  - `POST   /api/me/cv/sections/:section/items`           → aggiunge un item;
 *  - `DELETE /api/me/cv/sections/:section/items/:index`    → rimuove un item;
 *  - upload del Cv_Pdf (nessun transito del file dall'API):
 *      1. `POST /api/me/cv/upload { contentType: 'application/pdf' }`
 *         → `PresignedUpload` (URL firmato + chiave già persistita sullo User);
 *      2. `PUT` diretto del PDF su S3 verso `uploadUrl` con `Content-Type`
 *         combaciante (fetch nativo, non il client API: S3 valida la sola firma
 *         sul `Content-Type`);
 *      3. `POST /api/me/cv/analyze` → avvia l'analisi AI asincrona (`pending`).
 *
 * Polling: finché `aiStatus === 'pending'` l'analisi è in corso (worker SQS),
 * quindi la pagina ri-legge periodicamente `GET /api/me/cv` e si aggiorna da
 * sola, fermando il polling appena l'esito è definitivo (`completed`/`failed`),
 * come fa `ExternalCvDetail`.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 3.1, 5.1, 5.2, 5.5
 */
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../api/useApiClient';
import { useAuth } from '../auth/AuthProvider';
import {
  DEFAULT_SCORING_CONFIG,
  type AssessmentView,
  type DashboardDto,
  type EuropassCv,
  type ExternalCvAiStatus,
  type ExternalCvCertificationDto,
  type JobPositioningDto,
  type LinkedinCvImportResponse,
  type LinkedinCvImportSummary,
  type PresignedUpload,
  type ScoringConfigDto,
  type UserCertificationDto,
} from '../shared/dto';
import { ExternalCvProfileView } from '../components/ExternalCvProfileView';
import { normalizeCertificationName } from '../components/EuropassCvViewer';
import { Alert, Badge, Button, Loading, Modal, PageHeader, type BadgeTone } from '../components/ui';

/**
 * Vista aggregata del CV personale restituita da `GET /api/me/cv`. È un tipo
 * dell'API (definito lato backend in `myCvService.ts`): lo ridichiariamo qui
 * perché il frontend non condivide quel modulo. Rispecchia `MyCvDto` del
 * backend e riusa i sotto-tipi già presenti in `shared/dto`.
 */
interface MyCvDto {
  /** CV Europass dello User, oppure `null` in empty state (nessun Parsed_Cv). */
  parsedCv: EuropassCv | null;
  /** Chiave S3 del Cv_Pdf caricato, oppure `null` se nessun PDF è presente. */
  cvPdfKey: string | null;
  /** Stato dell'ultima analisi AI, oppure `null` se mai avviata. */
  aiStatus: ExternalCvAiStatus | null;
  dashboard: DashboardDto;
  assessments: AssessmentView[];
  jobPositioning: JobPositioningDto;
}

/** Chiave di cache della query del CV personale. */
const MY_CV_QUERY_KEY = ['me', 'cv'] as const;

/** Content-type dell'unico formato ammesso per il Cv_Pdf. */
const PDF_CONTENT_TYPE = 'application/pdf';

/**
 * Intervallo (ms) di polling mentre l'analisi AI è ancora in corso
 * (`aiStatus === 'pending'`). L'elaborazione è asincrona (worker SQS): dopo
 * l'avvio lo stato resta `pending` finché il worker non completa, quindi la
 * pagina ri-legge periodicamente e si aggiorna da sola, fermandosi appena
 * l'esito è definitivo.
 */
const PROCESSING_POLL_INTERVAL_MS = 4000;

/** Etichetta e tono della badge per ciascuno stato dell'analisi AI. */
const AI_STATUS_META: Record<ExternalCvAiStatus, { label: string; tone: BadgeTone }> = {
  pending: { label: 'Analisi del CV in corso', tone: 'warning' },
  completed: { label: 'Analisi del CV completata', tone: 'success' },
  failed: { label: 'Analisi del CV non riuscita', tone: 'danger' },
};

/**
 * Etichette leggibili in italiano per le Cv_Section Europass, usate nel
 * riepilogo (Import_Summary) dell'import da LinkedIn. Le chiavi corrispondono a
 * quelle di `EuropassCv`: il backend restituisce i soli nomi di sezione
 * (`populatedSections`/`preservedSections`), qui li traduciamo per l'utente.
 */
const CV_SECTION_LABELS: Record<keyof EuropassCv, string> = {
  personalInfo: 'Dati personali',
  summary: 'Profilo',
  workExperience: 'Esperienze',
  education: 'Istruzione',
  languages: 'Lingue',
  skills: 'Competenze',
  certifications: 'Certificazioni',
  projects: 'Progetti',
  awards: 'Riconoscimenti',
  publications: 'Pubblicazioni',
  detectedLanguage: 'Lingua del CV',
};

/**
 * Rende leggibile un elenco di nomi di Cv_Section tramite le etichette italiane
 * ({@link CV_SECTION_LABELS}), separati da virgola. Per una chiave non mappata
 * ripiega sul nome tecnico, così da non nascondere mai una sezione dall'esito.
 */
function formatCvSections(sections: (keyof EuropassCv)[]): string {
  return sections.map((s) => CV_SECTION_LABELS[s] ?? s).join(', ');
}

/**
 * Deriva nome e cognome da un `displayName` completo: la prima parola è il nome,
 * il resto il cognome. Serve a pre-compilare i dati personali del CV con
 * l'identità dell'utente loggato (che espone solo `displayName`). Restituisce
 * stringhe eventualmente vuote (nessuna assunzione sul formato del nome).
 */
function splitDisplayName(displayName: string): { firstName: string; lastName: string } {
  const parts = displayName.trim().split(/\s+/).filter((p) => p !== '');
  if (parts.length === 0) return { firstName: '', lastName: '' };
  const [firstName, ...rest] = parts;
  return { firstName, lastName: rest.join(' ') };
}

/**
 * Restituisce il `parsedCv` con i dati personali pre-compilati con nome e
 * cognome (dal `displayName`) ed email dell'utente loggato quando non sono
 * ancora valorizzati. Non muta l'input: se non c'è nulla da pre-compilare
 * ritorna il CV invariato. Gli altri campi dei dati personali restano
 * invariati; serve solo a non far reinserire all'utente la propria identità.
 */
function withLoggedUserIdentity(
  parsedCv: EuropassCv | null,
  displayName: string | undefined,
  email: string | undefined,
): EuropassCv | null {
  const { firstName, lastName } =
    displayName === undefined || displayName.trim() === ''
      ? { firstName: '', lastName: '' }
      : splitDisplayName(displayName);
  const trimmedEmail = email?.trim() ?? '';
  // Niente da pre-compilare: nessuna identità disponibile dall'utente loggato.
  if (firstName === '' && lastName === '' && trimmedEmail === '') return parsedCv;

  const cv: EuropassCv = parsedCv ?? {};
  const info = cv.personalInfo ?? {};
  // Pre-compila solo i campi mancanti: una modifica manuale dell'utente ha la
  // precedenza e non viene mai sovrascritta.
  const nextInfo = {
    ...info,
    ...(firstName !== '' &&
    (info.firstName === undefined || info.firstName.trim() === '')
      ? { firstName }
      : {}),
    ...(lastName !== '' &&
    (info.lastName === undefined || info.lastName.trim() === '')
      ? { lastName }
      : {}),
    ...(trimmedEmail !== '' &&
    (info.email === undefined || info.email.trim() === '')
      ? { email: trimmedEmail }
      : {}),
  };
  return { ...cv, personalInfo: nextInfo };
}

/**
 * Adatta una certificazione dell'utente (`UserCertificationDto`, da
 * `GET /api/me/certifications`) nella forma attesa dalla vista condivisa
 * (`ExternalCvCertificationDto`), così le certificazioni possedute sulla
 * piattaforma compaiono nella sezione "Certificazioni" del CV personale (oltre
 * a quelle eventualmente estratte dal PDF).
 *
 * Le skill collegate non sono risolte col nome dal catalogo in questa vista
 * (la card non le mostra se non valorizzate): si passa un elenco vuoto.
 */
function toCvCertification(cert: UserCertificationDto): ExternalCvCertificationDto {
  return {
    certificationId: cert.certificationId,
    name: cert.name,
    issuer: cert.issuer,
    imageUrl: cert.imageUrl,
    badgeUrl: cert.badgeUrl,
    issuedAt: cert.issuedAt,
    expiresAt: cert.expiresAt,
    skills: [],
  };
}

export default function MyCv() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { me } = useAuth();

  // Riferimento all'input file nascosto: il pulsante "Carica CV" lo attiva.
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Errore effimero del flusso di upload (presign → PUT S3 → analyze).
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Visibilità del modale che spiega cosa comporta il caricamento di un CV
  // (import sezioni, mappatura AI delle competenze, import certificazioni,
  // sovrascrittura dei dati anagrafici) prima di aprire il selettore file.
  const [confirmUploadOpen, setConfirmUploadOpen] = useState(false);
  // Esito dell'ultimo import da LinkedIn: il riepilogo (Import_Summary) mostrato
  // dopo un import riuscito. `null` finché non c'è un esito da mostrare.
  const [linkedinSummary, setLinkedinSummary] = useState<LinkedinCvImportSummary | null>(null);
  // Errore effimero dell'import da LinkedIn (canale disabilitato, token da
  // ricollegare, errore di rete/API). Mostrato senza modificare la vista del CV.
  const [linkedinImportError, setLinkedinImportError] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery<MyCvDto>({
    queryKey: MY_CV_QUERY_KEY,
    queryFn: () => api.get<MyCvDto>('/api/me/cv'),
    // Polling finché l'analisi AI è `pending`: si ferma (ritorna `false`)
    // appena lo stato diventa `completed`/`failed` (o è assente/null).
    refetchInterval: (query) =>
      query.state.data?.aiStatus === 'pending' ? PROCESSING_POLL_INTERVAL_MS : false,
  });

  // Certificazioni possedute sulla piattaforma (manuali, Credly, LinkedIn),
  // mostrate nella sezione "Certificazioni" del CV. Non blocca il render:
  // finché non è disponibile l'elenco è vuoto (come faceva la dashboard).
  // Configurazione di scoring: alimenta il dettaglio del calcolo dello Score
  // (apribile cliccando il badge Score di una skill). In attesa/errore ripiega
  // sui default applicativi, coerente con le altre viste che espongono il
  // dettaglio dello Score.
  const scoringConfigQuery = useQuery({
    queryKey: ['settings', 'scoring'],
    queryFn: () => api.get<ScoringConfigDto>('/api/settings/scoring'),
    staleTime: 5 * 60 * 1000,
  });
  const scoringConfig = scoringConfigQuery.data ?? DEFAULT_SCORING_CONFIG;

  const certificationsQuery = useQuery<UserCertificationDto[]>({
    queryKey: ['me', 'certifications'],
    queryFn: () => api.get<UserCertificationDto[]>('/api/me/certifications'),
  });
  // Certificazioni **visibili** mostrate nella sezione "Certificazioni" del CV.
  // Le nascoste dall'utente (`hidden: true`) sono escluse qui: compaiono solo
  // nella pagina "Le mie certificazioni", mai in "Il mio CV".
  const certifications = useMemo(
    () =>
      (certificationsQuery.data ?? [])
        .filter((c) => c.hidden !== true)
        .map(toCvCertification),
    [certificationsQuery.data],
  );
  // Nomi normalizzati delle certificazioni nascoste: servono a escludere anche
  // le loro eventuali copie estratte dal CV (una certificazione importata, es.
  // da LinkedIn, può comparire sia tra quelle della piattaforma sia nel CV
  // Europass). Senza questo filtro una nascosta ricomparirebbe come voce "del
  // CV" nella sezione "Altre certificazioni".
  const hiddenCertificationNames = useMemo(
    () =>
      new Set(
        (certificationsQuery.data ?? [])
          .filter((c) => c.hidden === true)
          .map((c) => normalizeCertificationName(c.name)),
      ),
    [certificationsQuery.data],
  );

  // CV da mostrare nella vista: come quello restituito dall'API ma senza le
  // certificazioni estratte dal PDF che corrispondono (per nome) a una
  // certificazione nascosta dall'utente, così una nascosta non ricompare come
  // voce "del CV" nella sezione "Altre certificazioni".
  const parsedCvForView = useMemo<EuropassCv | null>(() => {
    const cv = data?.parsedCv ?? null;
    if (cv === null) return null;
    const cvCerts = cv.certifications ?? [];
    if (cvCerts.length === 0 || hiddenCertificationNames.size === 0) return cv;
    return {
      ...cv,
      certifications: cvCerts.filter(
        (c) => !hiddenCertificationNames.has(normalizeCertificationName(c.name ?? '')),
      ),
    };
  }, [data?.parsedCv, hiddenCertificationNames]);

  /** Invalida la query del CV personale per rileggere lo stato aggiornato. */
  const invalidateMyCv = () =>
    queryClient.invalidateQueries({ queryKey: MY_CV_QUERY_KEY });

  // --- Mutazioni di editing delle sezioni (Editable_Mode) --------------------

  // Aggiornamento di un'intera sezione: `PUT /api/me/cv/sections` con body
  // `{ [section]: data }` (merge shallow lato API). Al termine rilegge il CV.
  const sectionUpdate = useMutation({
    mutationFn: (vars: { section: keyof EuropassCv; data: unknown }) =>
      api.put<EuropassCv>('/api/me/cv/sections', { [vars.section]: vars.data }),
    onSuccess: () => invalidateMyCv(),
  });

  // Aggiunta di un item a una sezione ripetibile:
  // `POST /api/me/cv/sections/:section/items` con il singolo item nel body.
  const itemAdd = useMutation({
    mutationFn: (vars: { section: string; item: unknown }) =>
      api.post<EuropassCv>(
        `/api/me/cv/sections/${encodeURIComponent(vars.section)}/items`,
        vars.item,
      ),
    onSuccess: () => invalidateMyCv(),
  });

  // Rimozione di un item da una sezione ripetibile:
  // `DELETE /api/me/cv/sections/:section/items/:index`.
  const itemRemove = useMutation({
    mutationFn: (vars: { section: string; index: number }) =>
      api.delete<EuropassCv>(
        `/api/me/cv/sections/${encodeURIComponent(vars.section)}/items/${vars.index}`,
      ),
    onSuccess: () => invalidateMyCv(),
  });

  // Callback passate alla vista condivisa: attendono il completamento della
  // mutazione (la vista usa il valore `Promise` per gestire il proprio stato di
  // salvataggio) e la query viene invalidata in `onSuccess`.
  const onSectionUpdate = async (section: keyof EuropassCv, data: unknown) => {
    await sectionUpdate.mutateAsync({ section, data });
  };
  const onItemAdd = async (section: string, item: unknown) => {
    await itemAdd.mutateAsync({ section, item });
  };
  const onItemRemove = async (section: string, index: number) => {
    await itemRemove.mutateAsync({ section, index });
  };

  // --- Upload del Cv_Pdf + avvio analisi AI ----------------------------------

  const uploadCv = useMutation({
    /**
     * Orchestrazione dell'upload presigned + avvio analisi:
     *  1. presign (persiste già la chiave sullo User lato API);
     *  2. PUT diretto del PDF su S3;
     *  3. avvio dell'analisi AI asincrona.
     */
    mutationFn: async (file: File): Promise<void> => {
      // 1) URL di upload firmato (solo application/pdf). L'API persiste la
      // chiave sullo UserDoc (un solo Cv_Pdf per utente).
      const presigned = await api.post<PresignedUpload>('/api/me/cv/upload', {
        contentType: PDF_CONTENT_TYPE,
      });

      // 2) PUT diretto su S3. NB: `fetch` nativo (non il client API), che non
      // deve allegare l'Authorization Bearer né alterare gli header: S3 valida
      // la firma sul solo `Content-Type`, che deve combaciare con quello firmato.
      const putResponse = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': presigned.contentType },
        body: file,
      });
      if (!putResponse.ok) {
        throw new Error(`Upload non riuscito (HTTP ${putResponse.status})`);
      }

      // 3) Avvia l'analisi AI asincrona: lo UserDoc passa a `aiStatus = pending`.
      await api.post<{ aiStatus: ExternalCvAiStatus }>('/api/me/cv/analyze');
    },
    onSuccess: () => {
      setUploadError(null);
      void invalidateMyCv();
    },
    onError: (err) => {
      setUploadError(
        err instanceof Error ? err.message : 'Caricamento del CV non riuscito. Riprova.',
      );
    },
  });

  // --- Import del CV da LinkedIn (sincrono) ----------------------------------

  // Import sincrono dei dati del profilo LinkedIn nel CV Europass del Self_User.
  // `POST /api/me/cv/import/linkedin` è self-only (lo `userId` è ricavato dalla
  // sessione JWT lato API, nessun parametro target) e restituisce l'esito nella
  // stessa richiesta: l'Import_Summary (sezioni popolate/conservate) e il
  // `parsedCv` aggiornato dopo il merge non distruttivo e il pruning.
  const importLinkedinCv = useMutation({
    mutationFn: () =>
      api.post<LinkedinCvImportResponse>('/api/me/cv/import/linkedin'),
    onSuccess: (result) => {
      // Mostra il riepilogo e rilegge il CV dall'API: coerente col resto della
      // pagina (invalidate su `MY_CV_QUERY_KEY`), la vista si aggiorna con il
      // `parsedCv` restituito senza manipolare direttamente la cache.
      setLinkedinImportError(null);
      setLinkedinSummary(result.summary);
      void invalidateMyCv();
    },
    onError: (err) => {
      // Messaggio esplicito dell'errore (canale disabilitato, token da
      // ricollegare, errore di rete/API) SENZA modificare la vista del CV: il
      // riepilogo precedente viene comunque rimosso per non confondere l'esito.
      setLinkedinSummary(null);
      setLinkedinImportError(
        err instanceof Error
          ? err.message
          : "Import da LinkedIn non riuscito. Riprova.",
      );
    },
  });

  // Il pulsante di upload non apre direttamente il selettore file: prima mostra
  // un modale che spiega le conseguenze del caricamento (import delle sezioni,
  // mappatura AI delle competenze del CV non ancora valutate, import delle
  // certificazioni, sovrascrittura dei dati anagrafici inseriti).
  const requestUpload = () => {
    setUploadError(null);
    setConfirmUploadOpen(true);
  };

  // Conferma dal modale: chiude il modale e apre il selettore file (l'input è
  // nascosto per usare il pulsante stilizzato).
  const confirmUpload = () => {
    setConfirmUploadOpen(false);
    fileInputRef.current?.click();
  };

  // Alla selezione di un file avvia il flusso di upload, poi resetta l'input per
  // consentire la riselezione dello stesso file.
  const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file === undefined) return;
    uploadCv.mutate(file);
  };

  // Analisi in corso: disabilita l'upload e dà feedback coerente con il polling.
  const isPending = data?.aiStatus === 'pending';
  const uploadBusy = uploadCv.isPending || isPending;

  // Visibilità del comando "Importa da LinkedIn" (Req 1.1).
  //
  // Il task richiede di mostrarlo solo se il Linkedin_Channel è "abilitato"
  // (`linkedinEnabled`, config) E "collegato" (`linkedinLinked`, UserDoc). Oggi
  // però il frontend NON dispone di un flag di config che esponga lo stato
  // "abilitato" del canale: l'unico segnale noto al client è `me.linkedinLinked`
  // (esposto da `/api/me`). Coerentemente col pattern esistente (vedi
  // `Profile.tsx` → `LinkedinBlock`, che condiziona la UI sul solo
  // `linkedinLinked`), condizioniamo qui la visibilità su `linkedinLinked` e
  // demandiamo la gestione del canale disabilitato all'errore esplicito
  // dell'API: un utente collegato ma con canale disabilitato che clicca riceve
  // l'errore 400 ("canale non configurato") mostrato senza modificare la vista,
  // soddisfacendo Req 1.3. Se in futuro verrà esposto al client un flag di
  // abilitazione, andrà messo in AND con `linkedinLinked` qui.
  const linkedinImportVisible = me?.linkedinLinked === true;

  // Input file nascosto, condiviso da tutti gli stati della pagina.
  const hiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept={PDF_CONTENT_TYPE}
      hidden
      onChange={onFileSelected}
    />
  );

  // Pulsante di upload del Cv_Pdf (etichetta coerente con lo stato corrente).
  const uploadButton = (
    <Button
      type="button"
      variant="primary"
      onClick={requestUpload}
      loading={uploadCv.isPending}
      disabled={uploadBusy}
    >
      {data?.parsedCv !== null && data?.parsedCv !== undefined
        ? 'Carica un nuovo CV'
        : 'Carica il tuo CV'}
    </Button>
  );

  // Comando "Importa da LinkedIn": reso solo quando il canale è collegato
  // (vedi `linkedinImportVisible`). In loading durante la chiamata; disabilitato
  // anche mentre un'analisi AI è in corso o è in corso un upload, coerente con
  // `uploadBusy` (si evita di sovrapporre importazioni concorrenti del CV).
  const linkedinImportButton = linkedinImportVisible ? (
    <Button
      type="button"
      variant="primary"
      onClick={() => importLinkedinCv.mutate()}
      loading={importLinkedinCv.isPending}
      disabled={importLinkedinCv.isPending || uploadBusy}
    >
      Importa da LinkedIn
    </Button>
  ) : null;

  if (isLoading) {
    return (
      <section aria-labelledby="my-cv-title">
        <PageHeader
          title="Il mio CV"
          titleId="my-cv-title"
          subtitle="Il tuo CV, le tue skill valutate e il tuo posizionamento."
        />
        <Loading>Caricamento del tuo CV…</Loading>
      </section>
    );
  }

  if (isError || data === undefined) {
    const message =
      error instanceof Error ? error.message : 'Impossibile caricare il tuo CV.';
    return (
      <section aria-labelledby="my-cv-title">
        <PageHeader title="Il mio CV" titleId="my-cv-title" />
        <Alert tone="error">{message}</Alert>
      </section>
    );
  }

  const status = data.aiStatus !== null ? AI_STATUS_META[data.aiStatus] : null;

  const header = (
    <PageHeader
      title="Il mio CV"
      titleId="my-cv-title"
      subtitle="Il tuo CV, le tue skill valutate e il tuo posizionamento."
      titleAdornment={status !== null ? <Badge tone={status.tone}>{status.label}</Badge> : undefined}
      actions={
        <div className="my-cv__actions">
          {uploadButton}
          {linkedinImportButton}
        </div>
      }
    />
  );

  // Banner tra header e sezioni: stato dell'analisi (pending) ed errori di upload.
  const banner = (
    <>
      {isPending && (
        <Alert tone="info">
          L'analisi AI del tuo CV è in corso. La pagina si aggiorna automaticamente
          al termine.
        </Alert>
      )}
      {uploadError !== null && <Alert tone="error">{uploadError}</Alert>}
      {/* Esito dell'import da LinkedIn: errore esplicito (senza modifiche alla
          vista) oppure riepilogo (Import_Summary) delle sezioni popolate e
          conservate. */}
      {linkedinImportError !== null && (
        <Alert tone="error" onDismiss={() => setLinkedinImportError(null)}>
          {linkedinImportError}
        </Alert>
      )}
      {linkedinSummary !== null && (
        <Alert tone="success" onDismiss={() => setLinkedinSummary(null)}>
          Import da LinkedIn completato.{' '}
          {linkedinSummary.populatedCount > 0 ? (
            <>
              {linkedinSummary.populatedCount}{' '}
              {linkedinSummary.populatedCount === 1
                ? 'sezione popolata'
                : 'sezioni popolate'}{' '}
              ({formatCvSections(linkedinSummary.populatedSections)}).
            </>
          ) : (
            <>Nessuna nuova sezione popolata.</>
          )}{' '}
          {linkedinSummary.preservedCount > 0 && (
            <>
              {linkedinSummary.preservedCount}{' '}
              {linkedinSummary.preservedCount === 1
                ? 'sezione già presente conservata'
                : 'sezioni già presenti conservate'}{' '}
              ({formatCvSections(linkedinSummary.preservedSections)}).
            </>
          )}
        </Alert>
      )}
    </>
  );

  // Empty state: nessun Parsed_Cv ancora disponibile. Si mostra comunque la vista
  // condivisa (skill valutate, competenze, professioni derivate) con un invito a
  // caricare un CV; la vista gestisce internamente le sezioni assenti.
  const parsedCvStatus: ExternalCvAiStatus = data.aiStatus ?? 'completed';

  return (
    <>
      {hiddenFileInput}

      {/* Modale informativo mostrato prima del selettore file: spiega cosa
          comporta il caricamento del CV. La conferma apre il file picker. */}
      <Modal
        open={confirmUploadOpen}
        onClose={() => setConfirmUploadOpen(false)}
        title="Cosa succede caricando il tuo CV"
        footer={
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmUploadOpen(false)}
            >
              Annulla
            </Button>
            <Button type="button" variant="primary" onClick={confirmUpload}>
              Ho capito, scegli il file
            </Button>
          </>
        }
      >
        <p>
          Caricando un CV in PDF, l'analisi AI estrarrà e importerà
          automaticamente le informazioni del documento:
        </p>
        <ul>
          <li>
            vengono importate le sezioni del CV (esperienza, istruzione, lingue,
            progetti e profilo professionale);
          </li>
          <li>
            le competenze trovate nel CV e non ancora valutate vengono mappate
            con l'AI sul catalogo delle skill;
          </li>
          <li>
            vengono importate le certificazioni rilevate nel documento.
          </li>
        </ul>
        <p>
          <strong>Attenzione:</strong> i dati anagrafici che hai inserito a mano
          (nome, cognome, contatti e informazioni personali) verranno
          sovrascritti con quelli estratti dal CV.
        </p>
      </Modal>

      <ExternalCvProfileView
        header={header}
        banner={banner}
        dashboard={data.dashboard}
        assessments={data.assessments}
        jobPositioning={data.jobPositioning}
        // Certificazioni possedute sulla piattaforma (adattate dal formato
        // `/api/me/certifications`), oltre a quelle estratte dal CV. Le nascoste
        // sono già escluse: non compaiono in "Il mio CV".
        certifications={certifications}
        // Tap su una competenza (principale, in griglia o tra le "altre") →
        // dettaglio competenza personale (`/assessment/competences/:id`). La
        // pagina di dettaglio torna già di default a "Il mio CV" (`/assessment`).
        competenceDetailPath={(competenceId) =>
          `/assessment/competences/${encodeURIComponent(competenceId)}`
        }
        // Tap su una certificazione della piattaforma → dettaglio personale
        // (`/assessment/certifications/:id`), sia nelle prime 3 card sia nella
        // tabella "Altre certificazioni". Le voci estratte dal CV non hanno id
        // né dettaglio, quindi restano non cliccabili.
        certificationDetailPath={(certificationId) =>
          `/assessment/certifications/${encodeURIComponent(certificationId)}`
        }
        // Provenienza: il dettaglio certificazione torna qui ("Il mio CV",
        // `/assessment`) invece che all'elenco delle certificazioni.
        certificationBackTo="/assessment"
        // Tap su una professione (in griglia o tra le "altre") → dettaglio
        // posizionamento personale (`/assessment/jobs/:id`). La provenienza
        // (`jobBackTo`) fa sì che il tasto "indietro" del dettaglio torni qui
        // ("Il mio CV", `/assessment`) invece che a "Le mie professioni".
        jobDetailPath={(jobId) => `/assessment/jobs/${encodeURIComponent(jobId)}`}
        jobBackTo="/assessment"
        // Dati personali pre-compilati con nome e cognome (derivati dal
        // `displayName`) ed email dell'utente loggato quando non ancora
        // valorizzati.
        parsedCv={withLoggedUserIdentity(parsedCvForView, me?.displayName, me?.email)}
        parsedCvStatus={parsedCvStatus}
        // Come nella dashboard: CTA "Valuta" nel box "Skill valutate" (porta al
        // wizard di autovalutazione) e KPI "Recensioni ricevute".
        evaluateHref="/assessment/skills/wizard"
        showReviewsReceived
        // Score cliccabile con il dettaglio del calcolo (incluse le review).
        scoringConfig={scoringConfig}
        editable
        onSectionUpdate={onSectionUpdate}
        onItemAdd={onItemAdd}
        onItemRemove={onItemRemove}
      />
    </>
  );
}
